import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, Landmark } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";

const money = (cents) => `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * TopUpDialog — move money from MRA's bank (on file with Stripe) into the
 * Stripe balance so a blocked grant batch can be re-authorized.
 */
export const TopUpDialog = ({ open, onOpenChange, suggestedCents = 0, onSubmit }) => {
  const [dollars, setDollars] = useState("");
  const [memo, setMemo] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setDollars(suggestedCents > 0 ? (Math.ceil(suggestedCents / 100)).toString() : "");
      setMemo(""); setBusy(false); setError("");
    }
  }, [open, suggestedCents]);

  const cents = Math.round(parseFloat(dollars || "0") * 100);
  const valid = cents >= 100 && cents <= 5_000_000;

  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await onSubmit({ amount_cents: cents, memo: memo.trim() || null });
      onOpenChange(false);
    } catch (e) {
      setError(e?.response?.data?.detail || "Stripe couldn't start the top-up. Try again in a moment.");
      setBusy(false);
    }
  };

  const inputCls = "mt-2 w-full rounded-md border border-border bg-bone px-4 py-3 text-sm text-ink placeholder:text-ink/30 focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="topup-dialog" className="max-w-lg border-border bg-sand p-0 gap-0 sm:rounded-lg">
        <div className="relative overflow-hidden bg-beige px-8 py-7">
          <span className="pointer-events-none absolute -right-12 -top-12 h-40 w-40 rounded-full bg-yellow/40 blur-3xl" />
          <div className="relative flex items-start gap-4">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ink text-sand"><Landmark className="h-5 w-5" strokeWidth={2} /></span>
            <div>
              <DialogTitle className="font-serif text-3xl font-normal leading-[1.05] text-ink">Add funds to Stripe</DialogTitle>
              <DialogDescription className="mt-2 text-sm leading-relaxed text-olive">
                Pulls from My Rich Aunt's bank account on file with Stripe (ACH debit). Funds usually arrive in about five business days — longer for the first few top-ups. You'll re-authorize the batch once they land.
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="space-y-5 px-8 py-6">
          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">Amount (USD)</span>
            <input data-testid="topup-amount" type="number" min="1" max="50000" step="1" inputMode="decimal" value={dollars} onChange={(e) => setDollars(e.target.value)} placeholder="2,500" className={inputCls} />
            {suggestedCents > 0 && <span className="mt-1 block text-xs text-olive">Shortfall for the selected batch: {money(suggestedCents)}</span>}
          </label>

          <label className="block">
            <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">Note for the books (optional)</span>
            <input data-testid="topup-memo" type="text" maxLength={140} value={memo} onChange={(e) => setMemo(e.target.value)} placeholder="Float for week ending Sep 12" className={inputCls} />
          </label>

          {error && (
            <p role="alert" data-testid="topup-error" className="flex items-start gap-2 rounded-md bg-scarlet/8 px-3 py-2 text-sm text-scarlet">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={2} /> {error}
            </p>
          )}

          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={() => onOpenChange(false)} disabled={busy} data-testid="topup-cancel" className="rounded-full border border-border bg-bone px-5 py-2.5 text-sm font-semibold text-ink transition-colors hover:border-ink disabled:opacity-60">
              Cancel
            </button>
            <button type="button" onClick={submit} disabled={!valid || busy} data-testid="topup-submit" className="inline-flex items-center gap-2 rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-sand transition-colors hover:bg-forest disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {valid ? `Move ${money(cents)} to Stripe` : "Add funds"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const TOPUP_STATUS = {
  pending:   { label: "In transit", cls: "bg-tangerine/15 text-tangerine" },
  succeeded: { label: "Landed",     cls: "bg-grass/15 text-grass" },
  failed:    { label: "Failed",     cls: "bg-scarlet/15 text-scarlet" },
  canceled:  { label: "Cancelled",  cls: "bg-ink/10 text-ink" },
  reversed:  { label: "Reversed",   cls: "bg-ink/10 text-ink" },
};
const day = (iso) => (iso ? new Date(`${iso}T12:00:00Z`).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : null);

/** Compact list of recent bank → Stripe top-ups. */
export const TopUpList = ({ items = [] }) => {
  if (!items.length) return null;
  return (
    <div className="mb-6 rounded-xl border border-border bg-bone px-5 py-3" data-testid="topup-list">
      <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-olive">Recent top-ups</p>
      <ul className="mt-2 divide-y divide-ink/10">
        {items.slice(0, 5).map((t) => {
          const s = TOPUP_STATUS[t.status] || TOPUP_STATUS.pending;
          return (
            <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`topup-row-${t.id}`}>
              <span className="font-serif text-lg text-ink">{money(t.amount_cents)}</span>
              <span className="text-xs text-olive">
                {t.status === "pending" && t.expected_availability ? `expected ${day(t.expected_availability)}` : t.failure_message || (t.memo ? `“${t.memo}”` : "")}
                {t.created_by_name ? ` · ${t.created_by_name}` : ""}
              </span>
              <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] ${s.cls}`} data-testid={`topup-status-${t.id}`}>{s.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
